import { Component, EventEmitter, Output } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { UserRoleEnum } from 'src/app/common/services/auth/enums/user-role.enum';
import { SkillService } from 'src/app/common/services/skill/skill.service';
import { StorageService } from 'src/app/common/services/storage/storage.service';
import { StatusEnum } from 'src/app/common/utils/status-enum.models';
import { markAllControlsAsDirty } from 'src/app/form-helper.ts';
import { ButtonColorEnum } from 'src/app/shared/buttons/enums/button-color.enum';
import { InputTypeEnum } from 'src/app/shared/input/enums/input-type.enum';
import { MaskTypeEnum } from 'src/app/shared/input/enums/mask-type.enum';
import { CategoryService } from '../../category/service/category.service';
import {
  GetCategoryRequest,
  GetCategoryResponseItem,
} from '../../category/service/models/category.service.model';
import {
  GetClientRequest,
  GetClientsResponse,
  GetSkillRequest,
  GetSkillResponse,
} from '../../client/models/client.model';
import { ClientService } from '../../client/service/client.service';
import {
  GetUserResponse,
  GetUserRequest,
} from '../../user/service/models/user.service.model';
import { UserService } from '../../user/service/user.service';
import { OffenderEnum } from '../enums/offender.enum';
import { GetKeywordsCloudRequest } from '../keywords-cloud.model';
import { PagedRequest } from 'src/app/paged-request.model';
import { Observable, tap, catchError, of } from 'rxjs';

@Component({
  selector: 'app-keywords-cloud-filter',
  templateUrl: './keywords-cloud-filter.component.html',
  styleUrls: ['./keywords-cloud-filter.component.scss'],
})
export class KeywordsCloudFilterComponent {
  @Output() filterEmmiter: EventEmitter<GetKeywordsCloudRequest> =
    new EventEmitter<GetKeywordsCloudRequest>();

  @Output() selectionChange = new EventEmitter<any>();

  form = this.formBuilder.group({
    startDate: ['', Validators.required],
    endDate: ['', Validators.required],
    cloudTheme: ['', Validators.required],
    attendants: [['']],
    clients: [['']],
    skills: [['']],
    cells: [[]],
    subCells: [[]],
    categories: [['']],
    shifts: [['']],
    types: [['']],
    contactReasonType: [''],
    offender: ['', Validators.required],
  });

  hasSupervisorRole: boolean = false;
  isValidPeriod: boolean = true;
  isOpen: boolean = true;
  isLoading: boolean = false;
  error: boolean = false;

  attendants: string[] = [];
  selectedAttendants: string[] = [];
  originalAttendants: GetUserResponse[] = [];

  clients: string[] = [];
  selectedClients: string[] = [];
  originalClients: GetClientsResponse[] = [];

  skills: string[] = [];
  selectedSkills: string[] = [];
  originalSkills: GetSkillResponse[] = [];

  categories: string[] = [];
  selectedCategories: string[] = [];
  originalCategories: GetCategoryResponseItem[] = [];

  loadingClients: boolean = false;
  loadingSearchClients: boolean = false;
  loadingMoreClients: boolean = false;
  totalItemsClients: number = 0;
  clientFilter = {
    pageNumber: 1,
    pageSize: 10,
    status: [StatusEnum.ATIVO],
    sortAscending: true,
    sortBy: 'Name',
  } as GetClientRequest;

  loadingCategories: boolean = false;
  loadingSearchCategories: boolean = false;
  loadingMoreCategories: boolean = false;
  totalItemsCategories: number = 0;
  categoryFilter = {
    pageNumber: 1,
    pageSize: 10,
    status: [StatusEnum.ATIVO],
    sortAscending: true,
    sortBy: 'Name',
  } as GetCategoryRequest;

  loadingSkills: boolean = false;
  loadingSearchSkills: boolean = false;
  loadingMoreSkills: boolean = false;
  totalItemsSkills: number = 0;
  skillsFilter = {
    pageNumber: 1,
    pageSize: 10,
    sortAscending: true,
    sortBy: 'Code',
  } as GetSkillRequest;

  loadingCells: boolean = false;
  loadingSearchCells: boolean = false;
  loadingMoreCells: boolean = false;
  totalItemsCells: number = 0;
  cells: string[] = [];
  selectedCells: string[] = [];
  cellsFilter = {
    pageNumber: 1,
    pageSize: 10,
    sortAscending: true,
    sortBy: 'Cell',
  } as PagedRequest;

  loadingSubcells: boolean = false;
  loadingSearchSubCells: boolean = false;
  loadingMoreSubCells: boolean = false;
  totalItemsSubCells: number = 0;
  selectedSubCells: string[] = [];
  subCells: string[] = [];
  subCellsFilter = {
    pageNumber: 1,
    pageSize: 10,
    sortAscending: true,
    sortBy: 'SubCell',
  } as PagedRequest;

  loadingSupervisors: boolean = false;
  loadingMoreSupervisors: boolean = false;
  totalItemsSupervisors: number = 0;
  supervisorsFilter = {
    pageNumber: 1,
    pageSize: 10,
    status: [StatusEnum.ATIVO],
    profiles: ['SUPERVISOR'],
    sortAscending: true,
    sortBy: 'Name',
  } as GetUserRequest;

  loadingAttendants: boolean = false;
  loadingSearchAttendants: boolean = false;
  loadingMoreAttendants: boolean = false;
  totalItemsAttendants: number = 0;
  attendantsFilter = {
    pageNumber: 1,
    pageSize: 10,
    status: [StatusEnum.ATIVO],
    profiles: ['ATENDENTE'],
    sortAscending: true,
    sortBy: 'Name',
  } as GetUserRequest;

  shifts: string[] = ['Manhã', 'Tarde', 'Noite', 'Madrugada'];
  selectedShifts: string[] = [];
  loadingShifts = false;

  types: string[] = ['Áudio', 'Chat'];
  selectedTypes: string[] = [];

  cloudThemes: string[] = [];
  loadingCloudThemes = false;

  contactReasonTypeOptions: string[] = ['Macro', 'Micro'];
  showContactReasonType: boolean = false;

  ButtonColorEnum = ButtonColorEnum;
  InputTypeEnum = InputTypeEnum;
  MaskTypeEnum = MaskTypeEnum;

  offenderOptions: string[] = ['Atendente', 'Cliente'];
  showOffender: boolean = false;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly storageService: StorageService,
    private readonly userService: UserService,
    private readonly categoryService: CategoryService,
    private readonly skillService: SkillService,
    private readonly clientService: ClientService,
    private readonly toastrService: ToastrService
  ) {}

  ngOnInit() {
    this.hasSupervisorRole =
      this.storageService.getRole() === UserRoleEnum.supervisor;

    this.form.controls.clients.valueChanges.subscribe(
      (value: string[] | null) => {
        if (value && value.length > 0) {
          const clientIds = this.getClientsIdFromText(value);

          const clients = this.originalClients.filter((s) =>
            clientIds.includes(s.id)
          );

          const filteredSkills = clients
            .map((s) => s.skills.map((a) => a))
            .flat();

          this.skills = filteredSkills.map(
            (i) => i.code + ': ' + i.cell + ' — ' + i.subCell
          );

          this.selectedSkills = [];
        } else {
          this.skills = this.originalSkills.map(
            (i) => i.code + ': ' + i.cell + ' — ' + i.subCell
          );
          this.selectedSkills = [];
        }

        if (
          !this.form.controls.clients.value ||
          this.form.controls.clients.value.length === 0 ||
          (this.form.controls.clients.value.length === 1 &&
            this.form.controls.clients.value[0] === '')
        ) {
          this.skills = this.originalSkills.map(
            (i) => i.code + ': ' + i.cell + ' — ' + i.subCell
          );
          this.selectedSkills = [];
        }
      }
    );

    this.form.controls.cloudTheme.valueChanges.subscribe(
      (value: string | null) => {
        if (value) {
          if (value == 'Ofensores') {
            this.showOffender = true;
            this.form.get('offender')?.setValidators([Validators.required]);
            this.form.get('offender')?.updateValueAndValidity();
          } else {
            this.showOffender = false;
            this.form.controls.offender.setValue(null);
            this.form.get('offender')?.clearValidators();
            this.form.get('offender')?.updateValueAndValidity();
          }

          if (value == 'Motivo de Contato') {
            this.showContactReasonType = true;
            this.form
              .get('contactReasonType')
              ?.setValidators([Validators.required]);
            this.form.get('contactReasonType')?.updateValueAndValidity();
          } else {
            this.showContactReasonType = false;
            this.form.controls.contactReasonType.setValue(null);
            this.form.get('contactReasonType')?.clearValidators();
            this.form.get('contactReasonType')?.updateValueAndValidity();
          }
        } else {
          this.showOffender = false;
          this.form.controls.offender.setValue(null);
          this.form.get('offender')?.clearValidators();
          this.form.get('offender')?.updateValueAndValidity();

          this.showContactReasonType = false;
          this.form.controls.contactReasonType.setValue(null);
          this.form.get('contactReasonType')?.clearValidators();
          this.form.get('contactReasonType')?.updateValueAndValidity();
        }
      }
    );
    markAllControlsAsDirty(this.form);

    this.populateCloudThemes();

    this.getClients(null);
    this.getSkills();
    this.getCells();
    this.getSubCells();
    this.getAttendants(null);
    this.getCategories(null);
  }

  populateCloudThemes() {
    this.loadingCloudThemes = true;
    this.cloudThemes = ['Analítica', 'Motivo de Contato', 'Ofensores'];
    this.loadingCloudThemes = false;
  }

  getCategories(name: string | null) {
    this.categoryFilter.pageNumber = 1;

    if (name) {
      this.categoryFilter.name = name;
      this.loadingSearchCategories = true;
    } else {
      this.loadingCategories = true;
      this.categoryFilter.name = null;
    }

    this.categoryService.get(this.categoryFilter).subscribe({
      next: (data) => {
        this.originalCategories = data.items;
        this.categories = data.items.map((category) =>
          category.client === null
            ? category.name
            : category.name + ' (' + category.client?.name + ') '
        );
        this.totalItemsCategories = data.totalItems;
        this.loadingCategories = false;
        this.loadingSearchCategories = false;
      },
      error: () => {
        this.toastrService.error('Não foi possível carregar as categorias.');
        this.loadingCategories = false;
        this.loadingSearchCategories = false;
      },
    });
  }

  getMoreCategories() {
    if (this.totalItemsCategories != this.categories.length) {
      this.loadingMoreCategories = true;

      if (this.categoryFilter.pageNumber) this.categoryFilter.pageNumber += 1;

      this.categoryService.get(this.categoryFilter).subscribe({
        next: (data) => {
          this.originalCategories = this.originalCategories.concat(data.items);
          this.totalItemsCategories = data.totalItems;

          this.categories = this.categories.concat(
            data.items.map((category) =>
              category.client === null
                ? category.name
                : category.name + ' (' + category.client?.name + ') '
            )
          );

          this.loadingMoreCategories = false;
        },
        error: () => {
          this.toastrService.error('Não foi possível carregar as categorias.');
          this.loadingMoreCategories = false;
        },
      });
    }
  }

  getClients(name: string | null) {
    this.clientFilter.pageNumber = 1;

    if (name) {
      this.clientFilter.name = name;
      this.loadingSearchClients = true;
    } else {
      this.loadingClients = true;
      this.clientFilter.name = null;
    }

    this.clientService.get(this.clientFilter).subscribe({
      next: (data) => {
        this.originalClients = data.items;
        this.clients = data.items.map((i) => i.name + ' | ' + i.cnpj);
        this.totalItemsClients = data.totalItems;
        this.loadingClients = false;
        this.loadingSearchClients = false;
      },
      error: () => {
        this.toastrService.error('Não foi possível carregar os clientes.');
        this.loadingClients = false;
        this.loadingSearchClients = false;
      },
    });
  }

  getMoreClients() {
    if (this.totalItemsClients != this.clients.length) {
      this.loadingMoreClients = true;
      if (this.clientFilter.pageNumber) this.clientFilter.pageNumber += 1;

      this.clientService.get(this.clientFilter).subscribe({
        next: (data) => {
          this.originalClients = this.originalClients.concat(data.items);
          this.totalItemsClients = data.totalItems;
          this.clients = this.clients.concat(
            data.items.map((i) => i.name + ' | ' + i.cnpj)
          );
          this.loadingMoreClients = false;
        },
        error: () => {
          this.toastrService.error('Não foi possível carregar os clientes.');
          this.loadingMoreClients = false;
        },
      });
    }
  }

  getSkills() {
    this.skillsFilter.pageNumber = 1;
    this.loadingSkills = true;

    this.skillService.get(this.skillsFilter).subscribe({
      next: (data) => {
        this.originalSkills = data.items;
        this.skills = data.items.map(
          (i) => i.code + ': ' + i.cell + ' — ' + i.subCell
        );
        this.totalItemsSkills = data.totalItems;
        this.loadingSkills = false;
      },
      error: () => {
        this.toastrService.error('Não foi possível carregar as skills.');
        this.loadingSkills = false;
      },
    });
  }

  getMoreSkills() {
    if (this.totalItemsSkills != this.skills.length) {
      this.loadingMoreSkills = true;
      if (this.skillsFilter.pageNumber) this.skillsFilter.pageNumber += 1;

      this.skillService.get(this.skillsFilter).subscribe({
        next: (data) => {
          this.originalSkills = this.originalSkills.concat(data.items);
          this.totalItemsSkills = data.totalItems;
          this.skills = this.skills.concat(
            data.items.map((i) => i.code + ': ' + i.cell + ' — ' + i.subCell)
          );
          this.loadingMoreSkills = false;
        },
        error: () => {
          this.toastrService.error('Não foi possível carregar as skills.');
          this.loadingMoreSkills = false;
        },
      });
    }
  }

  getCells() {
    this.cellsFilter.pageNumber = 1;
    this.loadingCells = true;

    this.skillService.getCells(this.cellsFilter).subscribe({
      next: (data) => {
        this.cells = data.items;
        this.totalItemsCells = data.totalItems;
        this.loadingCells = false;
      },
      error: () => {
        this.toastrService.error('Não foi possível carregar as células.');
        this.loadingCells = false;
      },
    });
  }

  getMoreCells() {
    if (this.totalItemsCells != this.cells.length) {
      this.loadingMoreCells = true;
      if (this.cellsFilter.pageNumber) this.cellsFilter.pageNumber += 1;

      this.skillService.getCells(this.cellsFilter).subscribe({
        next: (data) => {
          this.cells = this.cells.concat(data.items);
          this.totalItemsCells = data.totalItems;
          this.loadingMoreCells = false;
        },
        error: () => {
          this.toastrService.error('Não foi possível carregar as células.');
          this.loadingMoreCells = false;
        },
      });
    }
  }

  getSubCells() {
    this.subCellsFilter.pageNumber = 1;
    this.loadingSubcells = true;

    this.skillService.getSubCells(this.subCellsFilter).subscribe({
      next: (data) => {
        this.subCells = data.items;
        this.totalItemsSubCells = data.totalItems;
        this.loadingSubcells = false;
      },
      error: () => {
        this.toastrService.error('Não foi possível carregar as sub-células.');
        this.loadingSubcells = false;
      },
    });
  }

  getMoreSubCells() {
    if (this.totalItemsSubCells != this.subCells.length) {
      this.loadingMoreSubCells = true;
      if (this.subCellsFilter.pageNumber) this.subCellsFilter.pageNumber += 1;

      this.skillService.getSubCells(this.subCellsFilter).subscribe({
        next: (data) => {
          this.subCells = this.subCells.concat(data.items);
          this.totalItemsSubCells = data.totalItems;
          this.loadingMoreSubCells = false;
        },
        error: () => {
          this.toastrService.error('Não foi possível carregar as sub-células.');
          this.loadingMoreSubCells = false;
        },
      });
    }
  }

  getAttendants(name: string | null) {
    this.attendantsFilter.pageNumber = 1;

    if (name) {
      this.attendantsFilter.name = name;
      this.loadingSearchAttendants = true;
    } else {
      this.loadingAttendants = true;
      this.attendantsFilter.name = null;
    }

    this.userService.get(this.attendantsFilter).subscribe({
      next: (data) => {
        this.originalAttendants = data.items;
        this.attendants = data.items.map(
          (attendant) => attendant.name + ' | ' + attendant.functional
        );
        this.totalItemsAttendants = data.totalItems;
        this.loadingAttendants = false;
        this.loadingSearchAttendants = false;
      },
      error: () => {
        this.toastrService.error('Não foi possível carregar os atendentes.');
        this.loadingAttendants = false;
        this.loadingSearchAttendants = false;
      },
    });
  }

  getMoreAttendants() {
    if (this.totalItemsAttendants != this.attendants.length) {
      this.loadingMoreAttendants = true;
      if (this.attendantsFilter.pageNumber)
        this.attendantsFilter.pageNumber += 1;

      this.userService.get(this.attendantsFilter).subscribe({
        next: (data) => {
          this.originalAttendants = this.originalAttendants.concat(data.items);
          this.attendants = this.attendants.concat(
            data.items.map(
              (attendant) => attendant.name + ' | ' + attendant.functional
            )
          );
          this.totalItemsAttendants = data.totalItems;
          this.loadingMoreAttendants = false;
        },
        error: () => {
          this.toastrService.error('Não foi possível carregar os atendentes.');
          this.loadingMoreAttendants = false;
        },
      });
    }
  }

  getAttendantIdFromText(text: string): string | null {
    if (!text || text.trim() === '') return null;

    const [functionalString, name] = text.split('|').map((part) => part.trim());
    const attendant = this.originalAttendants.find(
      (attendant) =>
        attendant.functional.toString() === functionalString &&
        attendant.name === name
    );

    return attendant ? attendant.id : null;
  }

  getCategoriesIdFromName(categories: string[]): string[] {
    let categoryIds: string[] = [];

    categories.forEach((c) => {
      let categoryName = c.split('(')[0].trim();
      let category = this.originalCategories.find(
        (c) => c.name.trim() === categoryName.trim()
      );
      if (category) categoryIds.push(category.id);
    });

    return categoryIds;
  }

  getClientsIdFromText(clients: string[]): string[] {
    if (!clients || clients.length === 0) return [];
    let clientsId: string[] = [];
    clients.forEach((name) => {
      let clientName = name.split('|')[0].trim();
      let client = this.originalClients.find((i) => i.name.trim() === clientName.trim());
      if (client) clientsId.push(client.id);
    });

    return clientsId;
  }

  handleToggleAccordion() {
    this.isOpen = !this.isOpen;
  }

  clear() {
    this.form.reset();
    this.selectedAttendants = [];
    this.selectedCategories = [];
    this.selectedClients = [];
    this.selectedSkills = [];
    this.selectedShifts = [];
    this.selectedTypes = [];
  }

  getAttendantsIdFromText(text: string[]): string[] {
    let attendantIds: string[] = [];

    text.forEach((i) => {
      const [name, functionalString] = i.split('|').map((part) => part.trim());
      const attendant = this.originalAttendants.find(
        (j) =>
          j.functional.toString() === functionalString &&
          j.name.trim() === name.trim()
      );

      if (attendant) attendantIds.push(attendant.id);
    });

    return attendantIds;
  }

  getFilteredShiftsValue(text: string[] | null): string[] | null {
    if (!text || text.length == 0 || (text.length == 1 && text[0] == ''))
      return null;
    return this.form.controls.shifts.value;
  }

  getSkillIdFromText(text: string[]): string[] | null {
    if (!text || text.length === 0) return null;

    const skills: string[] = [];

    for (let i = 0; i < text.length; i++) {
      if (!text[i] || text[i].trim() === '') return null;

      const [code, cellAndSubCell] = text[i].split(':').map((i) => i.trim());
      const [cell, subCell] = cellAndSubCell.split('—').map((i) => i.trim());

      let skillFiltered = this.originalSkills.find(
        (i) =>
          i.code.toString() === code &&
          i.cell.trim() === cell &&
          i.subCell.trim() === subCell
      );

      if (!skillFiltered || skillFiltered == undefined) {
        const clients = this.form.controls.clients;

        if (
          clients &&
          clients.value &&
          clients.value.length > 0 &&
          clients.value[0] != ''
        ) {
          const filteredClients = this.originalClients.filter((client) =>
            clients.value?.includes(client.name)
          );

          skillFiltered = filteredClients
            .flatMap((client) => client.skills)
            .find(
              (skill) =>
                skill.code.toString() === code &&
                skill.cell.trim() === cell &&
                skill.subCell.trim() === subCell
            );

          if (!skillFiltered) return null;
        }
      }

      if (skillFiltered) skills.push(skillFiltered.id);
    }

    return skills;
  }

  getSkillsPlaceholder(): string {
    const clients = this.form.controls.clients.value;
    if (clients && clients.length > 0 && clients[0] !== '') {
      return this.skills.length > 0
        ? 'Selecione múltiplas skills'
        : 'Nenhuma skill disponível';
    }
    return 'Selecione múltiplas skills';
  }

  search() {
    if (this.isLoading) return;

    this.isLoading = true;
    this.error = false;
    let formError = false;

    if (
      this.form.controls.cloudTheme.value == null ||
      this.form.controls.cloudTheme.value == ''
    ) {
      this.form.controls.cloudTheme.markAsDirty();
      formError = true;
    }

    if (
      this.form.controls.endDate.value == null ||
      this.form.controls.endDate.value == ''
    ) {
      this.form.controls.endDate.markAsDirty();
      formError = true;
    }

    if (
      this.form.controls.startDate.value == null ||
      this.form.controls.startDate.value == ''
    ) {
      this.form.controls.startDate.markAsDirty();
      formError = true;
    }

    if (formError) {
      this.isLoading = false;
      return;
    }

    let filter: GetKeywordsCloudRequest = {} as GetKeywordsCloudRequest;

    filter.startDate = this.form.controls.startDate.value ?? '';
    filter.endDate = this.form.controls.endDate.value ?? '';

    filter.attendants = this.getAttendantsIdFromText(
      this.form.controls.attendants.value || []
    );

    filter.categories = this.getCategoriesIdFromName(
      this.form.controls.categories.value || []
    );

    filter.clients = this.getClientsIdFromText(
      this.form.controls.clients.value || []
    );

    filter.skills = this.getSkillIdFromText(
      this.form.controls.skills.value || []
    );

    filter.cells = this.form.controls.cells.value ?? [];

    filter.subcells = this.form.controls.subCells.value ?? [];

    filter.shifts = this.getFilteredShiftsValue(
      this.form.controls.shifts.value
    );

    filter.cloudTheme = this.form.controls.cloudTheme.value ?? '';

    let types: number[] | null;

    if (
      !this.form.controls.types?.value ||
      (this.form.controls.types.value.length == 1 &&
        this.form.controls.types.value[0].trim() == '')
    )
      types = null;
    else {
      types =
        this.form.controls.types.value?.map((i) => {
          return this.types.indexOf(i);
        }) ?? null;
    }

    filter.types = types;

    const offender = this.form.controls.offender.value;

    if (offender) {
      if (offender == 'Atendente') filter.offender = OffenderEnum.Attendant;
      else if (offender == 'Cliente') filter.offender = OffenderEnum.Client;
    }

    filter.contactReasonType =
      this.form.controls.contactReasonType.value ?? null;

    this.filterEmmiter.emit(filter);

    this.isLoading = false;
    this.isOpen = false;
  }
}
